import { useEffect, useRef, useState } from "react";
import { Button, Alert } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";

import supabase from "../utils/Supabase";
import { showToast } from "../utils/Toasts";
import { validateEmail } from "../utils/Text";
import { getUserInfo } from "../utils/UserUtil";
import {
  clearPatreonRefreshOnLogin,
  requestPatreonRefreshOnLogin,
} from "../utils/Patreon";

import useUserStore from "../stores/User";

import logo from "../img/sh-logo_site.png";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';
const TURNSTILE_ENABLED = !!TURNSTILE_SITE_KEY;

function DatabaseLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(TURNSTILE_ENABLED ? null : "bypass");
  const turnstileRef = useRef<TurnstileInstance>(null);
  const loggingInRef = useRef(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.has("redirectTo") ? `/checkRights?redirectTo=${searchParams.get("redirectTo")}` : "/checkRights";

  const isUserLoggedIn = useUserStore((state) => state.isLoggedIn());

  // Redirect if already logged in
  useEffect(() => {
    if (loggingInRef.current) {
      return;
    }
    if (isUserLoggedIn) {
      showToast("info", "Already logged in!");
      navigate(redirectTo);
    }
  }, [isUserLoggedIn, navigate]);

  async function signInWithEmail(email: string, password: string) {
    loggingInRef.current = true;
    // Login only: getUserInfo will call Patreon if this account is linked.
    // Ordinary page loads never set this flag.
    requestPatreonRefreshOnLogin();
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password,
      options: { captchaToken: TURNSTILE_ENABLED ? (captchaToken ?? undefined) : undefined },
    });

    if (data?.user) {
      try {
        await getUserInfo(data.user.id);
      } finally {
        clearPatreonRefreshOnLogin();
      }
      showToast("info", "Login successful!", 1000);
      navigate(redirectTo);
    }

    if (error) {
      loggingInRef.current = false;
      clearPatreonRefreshOnLogin();
      turnstileRef.current?.reset();
      setCaptchaToken(null);
      if (error.message === "User is banned") {
        setErrorMessage("Login failed. Please contact support.");
      } else if (error.message === "Email not confirmed") {
        setErrorMessage("You still need to confirm your email.");
      } else {
        setErrorMessage(error.message);
      }
    }
  }

  function validateLogin() {
      if (email.trim() !== "" && password.trim() !== "" && validateEmail(email)) {
        signInWithEmail(email, password)
      } else {
        setErrorMessage("Please enter a valid email and password.")
      };
  }

  if(!isUserLoggedIn){
    return (
      <main className="d-flex" style={{ flex: "max-content" }}>
        <div className="mx-3 my-auto mx-sm-auto">
          <img
            className="p-10 m-8 hivelogo"
            src={logo}
            alt="Script Hive logo"
            width={90}
          ></img>

          <p className="text-center mt-3">Please log in to access the database.</p>

          {errorMessage && (
            <Alert
              key="danger"
              variant="danger"
              className="mt-3"
              dismissible
              onClose={() => setErrorMessage("")}
            >
              {errorMessage}
            </Alert>
          )}

          <div className="d-flex flex-column form">
            <input
              type="email"
              className="mt-3 form-control"
              id="inputEmail"
              placeholder="example@email.com"
              onChange={function (e) {
                setErrorMessage("");
                setEmail(e.target.value);
              }}
            />

            <input
              type="password"
              className="mt-3 form-control"
              id="inputPassword2"
              placeholder="Password"
              onChange={function (e) {
                setErrorMessage("");
                setPassword(e.target.value);
              }}
            />

            {TURNSTILE_ENABLED && (
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                onError={() => {
                  setCaptchaToken(null);
                  turnstileRef.current?.reset();
                }}
                className="mt-3"
              />
            )}

            <Button
              className="mt-3 form-control"
              id="LogIn"
              onClick={validateLogin}
              disabled={TURNSTILE_ENABLED && captchaToken === null}
            >
              Log in
            </Button>

            <p className="mt-5">Don't have an account? Register for access now!</p>
            <Button
              className="form-control btn-secondary"
              id="ApplyForAccess"
              onClick={() => navigate("/register")}
            >
              Register
            </Button>

            <p className="mt-5">Forgot your password?</p>
            <Button
              className="form-control btn-warning"
              id="ResetPasswordBtn"
              onClick={() => navigate("/resetPassword")}
            >
              Reset your password
            </Button>
          </div>
        </div>
      </main>
    );
  } else {
    return(<></>);
  }
}

export default DatabaseLogin;
